import { useCallback, useEffect, useState } from 'react'
import { Monitor, Smartphone, Tablet } from 'lucide-react'
import api from '@/services/api'

type SessionEvent = {
  id: number
  event_type: string
  browser: string | null
  os: string | null
  device_type: string | null
  ip_address: string | null
  location: string | null
  auth_source: string | null
  created_at: string
}

function deviceIcon(deviceType: string | null) {
  if (deviceType === 'mobile') return <Smartphone size={13} className="text-stone-500" />
  if (deviceType === 'tablet') return <Tablet size={13} className="text-stone-500" />
  return <Monitor size={13} className="text-stone-500" />
}

function deviceLabel(event: SessionEvent) {
  const parts = [event.browser, event.os].filter(Boolean)
  return parts.length ? parts.join(' — ') : 'Unknown device'
}

export default function ActiveSessionsPanel() {
  const [sessions, setSessions] = useState<SessionEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<SessionEvent[]>('/auth/my-sessions')
      setSessions(res.data)
    } catch {
      setError('Unable to load recent sign-in activity.')
      setSessions([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  if (loading) {
    return <div className="text-[12px] text-stone-500">Loading sessions…</div>
  }

  if (error) {
    return <div className="rounded border border-red-400/20 bg-red-400/10 px-3 py-2 text-[12px] text-red-300">{error}</div>
  }

  if (sessions.length === 0) {
    return (
      <div className="rounded-lg border border-amber-400/10 bg-black/20 px-4 py-6 text-center text-[12px] text-stone-500">
        No recent sign-in activity recorded for this account.
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-amber-400/10 bg-black/30 overflow-hidden">
      <table className="dense-table">
        <thead>
          <tr>
            <th>Device</th>
            <th>IP Address</th>
            <th>Location</th>
            <th>Last Active</th>
            <th>Method</th>
          </tr>
        </thead>
        <tbody>
          {sessions.map((session, index) => (
            <tr key={session.id}>
              <td className="flex items-center gap-2">
                {deviceIcon(session.device_type)}
                <span>{deviceLabel(session)}</span>
              </td>
              <td className="font-mono text-[11px]">{session.ip_address ?? '—'}</td>
              <td>{session.location ?? '—'}</td>
              <td>{new Date(session.created_at).toLocaleString()}</td>
              <td className="text-[11px] text-stone-400">
                {index === 0 ? 'Current session' : (session.auth_source ?? session.event_type)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
